// Status composable — overall system status computation
import { computed } from 'vue'
import { useEvents } from './useEvents'
import { useI18n } from './useI18n'
import type { Entry } from '@/types'

export function useStatus() {
  const { allEntries, servicesList, findServiceByName, findComponentByName, getServiceLabel, getComponentLabel } = useEvents()
  const { lang } = useI18n()

  const activeIncidents = computed(() =>
    allEntries.value.filter(
      (e) =>
        e.type === 'incident' &&
        (e.status === 'investigating' || e.status === 'identified' || e.status === 'monitoring')
    )
  )

  const overallSeverity = computed(() => {
    if (activeIncidents.value.length === 0) return 'operational'
    if (activeIncidents.value.some((e) => e.severity === 'critical')) return 'critical'
    if (activeIncidents.value.some((e) => e.severity === 'major')) return 'major'
    return 'minor'
  })

  const statusKey = computed(() => {
    switch (overallSeverity.value) {
      case 'critical':
        return 'status-major'
      case 'major':
        return 'status-partial'
      case 'minor':
        return 'status-degraded'
      default:
        return 'status-operational'
    }
  })

  const statusClass = computed(() => {
    switch (overallSeverity.value) {
      case 'critical':
        return 'major'
      case 'major':
        return 'partial'
      case 'minor':
        return 'degraded'
      default:
        return ''
    }
  })

  const affectedServicesAndComponents = computed(() => {
    const affected: Record<string, { type: string; name: string; severity: string; service?: string }> = {}

    activeIncidents.value.forEach((incident) => {
      if (incident.service) {
        const key = `service:${incident.service}`
        if (
          !affected[key] ||
          incident.severity === 'critical' ||
          (incident.severity === 'major' && affected[key].severity !== 'critical')
        ) {
          affected[key] = { type: 'service', name: incident.service, severity: incident.severity }
        }

        if (incident.component) {
          const compKey = `component:${incident.component}`
          if (
            !affected[compKey] ||
            incident.severity === 'critical' ||
            (incident.severity === 'major' && affected[compKey].severity !== 'critical')
          ) {
            affected[compKey] = {
              type: 'component',
              name: incident.component,
              severity: incident.severity,
              service: incident.service,
            }
          }
        }
      }
    })

    return Object.values(affected)
  })

  return {
    activeIncidents,
    overallSeverity,
    statusKey,
    statusClass,
    affectedServicesAndComponents,
  }
}
